import React, { useState, useRef } from 'react';
import './InspectionComments.css';

const InspectionComments = ({ 
    inspectionId, 
    inspectionComments = { comments: [], attachments: [] }, 
    onCommentSubmit,
    onDownloadFile,
    isDisabled = false
}) => {
    
    const [newComment, setNewComment] = useState('');
    const [pendingFiles, setPendingFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });
    
    const escapeCommentHtml = (value) => String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const commentHtml = (comment) => {
        const content = comment.content || '';
        const safeContent = comment.type === 'information-correction'
            ? escapeCommentHtml(content)
            : content;
        return safeContent.replace(/\n/g, '<br>');
    };

    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const dropZoneRef = useRef(null);


    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    // Manejar archivos adjuntos    
    const handleFileSelect = (files) => {
        const fileArray = Array.from(files);
        const validFiles = fileArray.filter(file => {
            const maxSize = 20 * 1024 * 1024; // 20MB
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.oasis.opendocument.text',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'image/jpeg',
                'image/png',
                'image/gif'
            ];
            
            if (file.size > maxSize) {
                showNotification(`El archivo ${file.name} es muy grande (máximo 20MB)`, 'error');
                return false;
            }
            
            if (!allowedTypes.includes(file.type)) {
                showNotification(`El archivo ${file.name} no es un tipo válido`, 'error');
                return false;
            }
            
            return true;
        });

        setPendingFiles(prev => [...prev, ...validFiles]);
    };

    const handleFileInputChange = (e) => {
        handleFileSelect(e.target.files);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        handleFileSelect(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const removePendingFile = (index) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Subir comentario
    const handleSubmitComment = async () => {
        if (!newComment.trim() && pendingFiles.length === 0) {
            showNotification('Agrega un comentario o archivo adjunto', 'error');
            return;
        }

        if (!onCommentSubmit) {
            showNotification('Error: función de envío no disponible', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const commentData = {
                content: newComment.trim()
            };
            // Limpiar formulario
            setNewComment('');
            setPendingFiles([]);

            await onCommentSubmit(commentData, pendingFiles);

        
        } catch (error) {
            console.error('Error submitting comment:', error);
            showNotification('Error al enviar comentario', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Formatear fecha
    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString('es-CO', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="inspection-comments">
            <div className="comments-header">
                <h3>Comentarios de la Inspección</h3>
                <span className="comments-count">{inspectionComments.comments.length} comentarios</span>
            </div>

            {/* Lista de comentarios */}
            <div className="comments-list">
                {inspectionComments.comments.length === 0 ? (
                    <div className="no-comments">
                        <p>No hay comentarios aún. Sé el primero en comentar.</p>
                    </div>
                ) : (
                    inspectionComments.comments.map((comment, index) => (
                        <div key={comment.id || index} className={`comment-item${comment.type === 'information-correction' ? ' comment-item-correction' : ''}`}>
                            <div className="comment-header">
                                <div className="comment-author">
                                    <span className="author-name">
                                        {comment.author.name}
                                        {comment.type === 'information-correction' && (
                                            <span className="comment-correction-badge">Corrección</span>
                                        )}
                                    </span>
                                    <span className="author-role">{comment.author.role}</span>
                                </div>
                                <div className="comment-date">
                                    {formatDate(comment.timestamp)}
                                </div>
                            </div>
                            <div 
                                className="comment-content"
                                dangerouslySetInnerHTML={{ 
                                    __html: commentHtml(comment) 
                                }}
                            />
                            {comment.attachments && comment.attachments.length > 0 && (
                                <div className="comment-attachments">
                                    {comment.attachments.map((attachment, attIndex) => (
                                        <button
                                            key={attIndex}
                                            className="attachment-item"
                                            onClick={() => onDownloadFile && onDownloadFile(attachment)}
                                        >
                                            <span className="attachment-icon">📎</span>
                                            <span className="attachment-name">{attachment.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Archivos adjuntos globales */}
            {inspectionComments.attachments.length > 0 && (
                <div className="global-attachments">
                    <h4>Archivos Adjuntos</h4>
                    <div className="attachments-list">
                        {inspectionComments.attachments.map((attachment, index) => (
                            <button
                                key={attachment.id || index}
                                className="attachment-item"
                                onClick={() => onDownloadFile && onDownloadFile(attachment)}
                            >
                                <span className="attachment-icon">📎</span>
                                <span className="attachment-name">{attachment.name}</span>
                                <span className="attachment-size">
                                    {(attachment.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Editor de comentarios */}
            <div className={`comment-editor ${isDisabled ? 'disabled' : ''}`}>

                <div className="editor-content">
                    <textarea
                        ref={textareaRef}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Escribe tu comentario aquí..."
                        className="comment-textarea"
                        rows="4"
                    />
                </div>

                {/* Zona de archivos adjuntos */}
                <div 
                    ref={dropZoneRef}
                    className="file-drop-zone"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    />
                    <div className="drop-zone-content">
                        <span className="drop-zone-icon">📁</span>
                        <span className="drop-zone-text">
                            Arrastra archivos aquí o haz clic para seleccionar
                        </span>
                    </div>
                </div>

                {/* Archivos pendientes */}
                {pendingFiles.length > 0 && (
                    <div className="pending-files">
                        <h5>Archivos pendientes:</h5>
                        {pendingFiles.map((file, index) => (
                            <div key={index} className="pending-file-item">
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">
                                    {(file.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                                <button
                                    type="button"
                                    className="remove-file-btn"
                                    onClick={() => removePendingFile(index)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="editor-actions">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmitComment}
                        disabled={isSubmitting || (!newComment.trim() && pendingFiles.length === 0)}
                    >
                        {isSubmitting ? 'Guardando...' : 'Agregar Comentario'}
                    </button>
                </div>
            </div>

            {/* Notificación */}
            {notification.visible && (
                <div className={`notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}
        </div>
    );
};

export default InspectionComments;